import { call, put, takeLatest, takeEvery, all } from 'redux-saga/effects';
import {
  MEDIA_LIBRARY_FETCH_REQUEST, MEDIA_LIBRARY_UPLOAD_REQUEST, MEDIA_LIBRARY_DELETE_REQUEST,
  type MediaLibraryFetchRequestPayload, type MediaLibraryUploadRequestPayload, type MediaLibraryDeleteRequestPayload,
} from './mediaLibraryTypes';
import { mediaLibraryFetchSuccess, mediaLibraryFetchFailure, mediaLibraryAdd, mediaLibraryDelete } from './mediaLibraryActions';
import { uploadMediaLibraryApi, fetchMediaLibraryApi, deleteMediaLibraryApi, type MediaLibraryUploadApiResponse, type MediaLibraryListItemApi, type MediaLibraryDeleteApiResponse } from './mediaLibraryApi';
import type { MediaKind, MediaLibraryItem } from '../../../components/display/data/types';

const mapListItem = (item: MediaLibraryListItemApi): MediaLibraryItem => ({
  id: item.id,
  name: item.name,
  mimeType: item.mimeType,
  kind: (item.mimeType || '').startsWith('video/') ? 'video' : 'image',
  url: item.url,
  sizeBytes: item.sizeBytes,
  orientation: item.orientation === 'LANDSCAPE' ? 'Landscape' : 'Portrait',
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
});

/** Real backend: GET /display/media?locationId= */
function* fetchMediaLibrarySaga(action: { type: string; payload: MediaLibraryFetchRequestPayload }): Generator<any, void, any> {
  try {
    const res: { data: { data: MediaLibraryListItemApi[] } } = yield call(fetchMediaLibraryApi, action.payload.locationId);
    const items = (res.data?.data ?? []).map(mapListItem);
    yield put(mediaLibraryFetchSuccess(items));
  } catch (err: any) {
    yield put(mediaLibraryFetchFailure(err?.message || 'Failed to load media library'));
  }
}

/** Real backend: POST /display/upload-media. On success, inserts the uploaded item
 *  (with its server-issued mediaId/url) into local state via the existing ADD reducer. */
function* uploadMediaSaga(action: { type: string; payload: MediaLibraryUploadRequestPayload }): Generator<any, void, any> {
  const { locationId, file, orientation, deferred, onProgress } = action.payload;
  try {
    const apiOrientation = orientation === 'Portrait' ? 'PORTRAIT' : 'LANDSCAPE';
    const res: { data: MediaLibraryUploadApiResponse } = yield call(uploadMediaLibraryApi, locationId, file, apiOrientation, { onProgress });
    const data = res.data;
    const kind: MediaKind = (data.mimeType || file.type).startsWith('video/') ? 'video' : 'image';
    const uploaded = {
      id: data.mediaId,
      name: file.name,
      mimeType: data.mimeType || file.type,
      kind,
      url: data.url,
      sizeBytes: data.sizeBytes ?? file.size,
      orientation: data.orientation === 'PORTRAIT' ? ('Portrait' as const) : ('Landscape' as const),
    };
    yield put(mediaLibraryAdd(uploaded));
    deferred.resolve(uploaded);
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to upload media'));
  }
}

/** Real backend: DELETE /display/media/{id}?locationId= — 409 (nothing deleted) if the media
 *  is in use by an active display or any playlist; local state stays untouched on failure. */
function* deleteMediaSaga(action: { type: string; payload: MediaLibraryDeleteRequestPayload }): Generator<any, void, any> {
  const { id, locationId, deferred } = action.payload;
  try {
    const res: { data: { data: MediaLibraryDeleteApiResponse } } = yield call(deleteMediaLibraryApi, id, locationId);
    if (res.data?.data?.deleted === false) throw new Error('Media was not deleted');
    yield put(mediaLibraryDelete(id));
    deferred.resolve();
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to delete media'));
  }
}

export function* mediaLibrarySaga(): Generator<any, void, any> {
  yield all([
    takeLatest(MEDIA_LIBRARY_FETCH_REQUEST, fetchMediaLibrarySaga),
    // takeEvery — multiple files can be uploaded/deleted concurrently; takeLatest would cancel earlier calls.
    takeEvery(MEDIA_LIBRARY_UPLOAD_REQUEST, uploadMediaSaga),
    takeEvery(MEDIA_LIBRARY_DELETE_REQUEST, deleteMediaSaga),
  ]);
}
